/**
 * Quote contracts.
 *
 * A quote is a structured pre-invoice artifact built by a technician for a
 * customer. It contains one or more "items" (groups), and each item has
 * multiple "options" the customer chooses between. When the customer
 * accepts, the quote is "promoted": a new invoice is generated whose line
 * items are the chosen option of each marked item.
 *
 * Storage is Dataverse-only this phase (no RS/Xero sync). The items+options
 * tree is persisted as a JSON blob on the cr001_quotes row, mirroring the
 * cr001_line_items pattern on cr001_jobs.
 */

import type { ExtractedSpecs } from './distiListingResult';
import type { RepairDeviceType } from './repairServiceType';

/**
 * Quote lifecycle status.
 *
 *  - `requested` — a technician has raised a request for a quote that
 *    another user needs to pick up and complete. Used as an in-tray:
 *    the requesting tech captures the customer + brief context, and a
 *    quoter takes ownership to flesh out items/services and move it to
 *    `draft`. Not a customer-facing state.
 *  - `draft` — being worked on; not yet sent to the customer.
 *  - `sent` — delivered to the customer; awaiting their decision.
 *  - `accepted` — customer accepted; promotion to invoice expected
 *    (set automatically on successful promote).
 *  - `declined` — customer rejected.
 *  - `expired` — past `validUntil` without a decision.
 */
export type QuoteStatus =
  | 'requested'
  | 'draft'
  | 'sent'
  | 'accepted'
  | 'declined'
  | 'expired';

/**
 * Quote category — discriminates the workflow + reporting bucket the
 * quote belongs to.
 *
 *  - `new_laptop` / `new_desktop` / `new_desktop_custom` / `new_all_in_one` —
 *    quoting a new computer via the disti picker or retailer URL flow.
 *    The four sub-types replaced the legacy `replacement` bucket so the
 *    title and inventory scope can be tailored per form factor.
 *  - `repair` — quoting work for a repair ticket. Repair services on this
 *    quote can mix labour, parts, third-party sub-contracts, and flat-rate
 *    `fixed_price` lines on the same quote — see `RepairServiceComponent.kind`.
 *  - `network_install` — quoting a network installation (switches, router /
 *    firewall, wireless access points, cabling, plus pre-install
 *    configuration and onsite labour). Reuses the repair-service shape
 *    (`repairServices` + components) and collapses to one bundled
 *    "Network Installation" customer line on promotion. Unlike `repair`
 *    it has no device / check-in / diagnostic-fee semantics.
 *  - `m365` — Microsoft 365 / cloud onboarding quote (license SKUs
 *    + onboarding labour). Tagged for reporting today; type-specific
 *    catalog / picker work follows in a later phase.
 *  - `replacement` — legacy "New Computer" type retained for back-compat
 *    on existing rows. Hidden from new-quote dropdowns.
 */
export type QuoteType =
  | 'new_laptop'
  | 'new_desktop'
  | 'new_desktop_custom'
  | 'new_all_in_one'
  | 'repair'
  | 'network_install'
  | 'm365'
  | 'replacement';

/**
 * A single sourcing/pricing option the customer can choose from for a
 * given item-group.
 */
export interface QuoteOption {
  id: string;                 // uuid
  description: string;        // customer-facing description
  buyPriceExGst: number;      // supplier cost, ex-GST
  shipping: number;           // freight component
  url: string;                // supplier link
  sellPrice: number;          // customer-facing price, per-unit, ex-GST
  productCode: string;        // CatalogProduct.sku — used as invoice line name on promotion

  /**
   * Whole units of this option, ex-GST per-unit pricing.
   *
   * `sellPrice` and `buyPriceExGst` are per-unit; `shipping` is
   * per-line (freight charged once, not per unit). The customer-facing
   * line total (ex-GST) is `round2(quantity * sellPrice)`.
   *
   * Optional with a default of 1: missing / null / < 1 / non-finite all
   * mean a single unit. Legacy options carry no quantity, so a quote
   * with qty-1 options round-trips with line total == sellPrice.
   *
   * Repair / network-install quotes model quantity via
   * `RepairServiceComponent.units` instead — this field only applies to
   * item/option (product-type) quotes.
   */
  quantity?: number;

  /**
   * Optional internal note about this option — free text the tech can
   * jot to record supplier caveats, stock / lead-time warnings,
   * "preferred supplier", etc.
   *
   * NEVER shown to the customer and NEVER promoted onto the invoice:
   * it is deliberately excluded from the customer-facing preview
   * (`QuoteHtmlPreviewDialog`) and from invoice-line promotion, which
   * both map an explicit set of option fields and do not include this
   * one. Keep it that way if you touch those paths.
   *
   * Null / undefined / empty string all mean "no note".
   */
  note?: string | null;

  /**
   * Device-type tag for retail-sourced options (laptop / desktop /
   * all_in_one / …). Picked from the admin-managed
   * `AppSettings.deviceTypes` catalog in the "Add retail option"
   * dialog — the picked entry's `productCode` drives the `productCode`
   * field above, and `deviceType` is stored here so downstream
   * filters/pickers can scope by device class without re-parsing
   * the product code string.
   *
   * Null/undefined for hand-entered options, repair quote-options
   * picked before the device-type field existed, and disti-sourced
   * options on listings that haven't been backfilled yet.
   */
  deviceType?: string | null;

  /**
   * Snapshot of the product image URL captured at pick time. Rendered in
   * the customer-facing preview's 3-column layout (image · description ·
   * price). Null/undefined for hand-entered options or sources that don't
   * carry an image.
   */
  imageUrl?: string | null;

  /**
   * Back-pointer to the disti listing this option was picked from
   * (when the option was sourced from the listings picker rather
   * than typed in by hand or copied from the price-book search).
   *
   * Snapshot semantics: the option's price/description/etc. fields
   * are NOT auto-updated when the source listing changes — but the
   * quote-detail render uses these to look up the live listing and
   * surface "source price moved / stale / removed" chips.
   *
   * Both fields null/undefined for hand-entered options or those
   * picked from the price-book (cross-quote past options).
   */
  sourceDistiId?: string | null;
  sourceDistiSku?: string | null;

  /**
   * Snapshot of the extracted specs from the source listing, captured
   * at pick time. Used by the quote detail view to render a structured
   * spec line under the option's description so the tech can verify
   * the parsed CPU / RAM / storage / OS without re-fetching the source
   * listing. Stays valid even if the source listing is later removed
   * or rewritten by the disti.
   *
   * Null/undefined for hand-entered options or those picked from the
   * price-book search (which doesn't carry structured specs).
   */
  sourceSpecs?: ExtractedSpecs | null;
}

/**
 * An item-group on a quote. Customer picks one option per item.
 */
export interface QuoteItem {
  id: string;                 // uuid
  name: string;               // group label, e.g. "Monitor"
  position: number;           // display order
  selectedOptionId: string | null;
  markedForInvoice: boolean;  // included when the quote is promoted
  options: QuoteOption[];

  /**
   * Build-category slug for New Desktop (Custom) quotes. Identifies which
   * component slot this item belongs to on the build sheet
   * (`cpu` | `cpu_cooler` | `case` | `motherboard` | `gpu` | `psu` | `ram`
   *  | `case_fans` | `os` | `storage` | `other`), independent of the
   * editable `name` label. The `DesktopBuildEditor` groups items by this
   * slug and renders one card per category; "allow multiple" categories
   * (gpu / ram / case_fans / storage) hold several items sharing a slug,
   * each an included part in the build (NOT alternatives — the whole build
   * collapses to one consolidated line).
   *
   * Null/undefined for every other quote type (which use the freeform
   * items/options tree) and for legacy custom-desktop rows created before
   * the build sheet existed.
   */
  category?: string | null;

  /**
   * Billing group membership. Marked items that share a `billingGroupId`
   * collapse into ONE customer-facing invoice/quote line — the "group items
   * into a single billable line" feature. The line's price is the SUM of the
   * grouped items' selected-option line totals; the individual items are not
   * shown to the customer as separate priced lines.
   *
   * Applies to item/option quotes generally (QuoteEditor + the New Desktop
   * (Custom) build sheet). Ungrouped marked items still bill as their own
   * line. A `consolidated` quote (all-one-line) ignores groups; a Custom
   * Desktop build is one consolidated line UNTIL any group exists, at which
   * point it bills as its group line(s) + any ungrouped parts.
   *
   * `null`/undefined = the item bills on its own line (the default). A group
   * with only one member just bills as a normal line.
   */
  billingGroupId?: string | null;

  /**
   * Customer-facing line name for this item's billing group (e.g. "Core
   * Components"). Denormalised onto every member item and kept in sync by the
   * editor, so line-building can read it without a separate groups table.
   * Blank/undefined → the line falls back to the joined member descriptions.
   * Only meaningful when `billingGroupId` is set.
   */
  billingGroupName?: string | null;

  /**
   * Optional customer-facing description for the billing-group line,
   * denormalised across the group's members like `billingGroupName`.
   * `null`/undefined → the line auto-composes `<item>, <item>…` from the
   * grouped items. selected-option descriptions. A non-empty string
   * is used verbatim. Only meaningful when `billingGroupId` is set.
   */
  billingGroupDescription?: string | null;
}

// ---------------------------------------------------------------------------
// Repair-service shape — used when quote.type === 'repair' or
// quote.type === 'network_install' (both store their line items as
// `repairServices` + components and promote via the same path).
// ---------------------------------------------------------------------------

/**
 * One internal component (labour or part) sitting under a repair service.
 * Never shown to the customer — these only justify the rolled-up customer
 * price (sum of component sellPrices). Labour entries typically have no
 * productCode; part entries link back to a catalog SKU when known.
 */
/**
 * Default catalog SKUs new repair-service components are seeded with. Both
 * are user-overridable per line — these only define what auto-populates
 * when the tech clicks "Add labour" / "Add part".
 *
 *   - `RCS.RepairLabour` — customer-facing labour SKU. Its catalog
 *     `unitCost` is read as the default hourly rate.
 *   - `HW.RepairPart`    — internal SKU marking a hardware item bought
 *     specifically for a repair (i.e. not stock). The actual part is
 *     described in `description` + `url`.
 */
export const RCS_REPAIR_LABOUR_SKU = 'RCS.RepairLabour';
export const HW_REPAIR_PART_SKU = 'HW.RepairPart';

/**
 * Service code for the single bundled customer line on a
 * `network_install` quote. The auto-seeded "Network Installation"
 * service uses this as its `repairServiceCode`, so it becomes the
 * invoice line name on promotion. Matches the admin-managed
 * RepairServiceType catalog row of the same code.
 */
export const RCS_NETWORK_INSTALL_SKU = 'RCS.NetworkInstall';

export interface RepairServiceComponent {
  id: string;                       // uuid
  /**
   * Component shape discriminator.
   *
   *  - `labour`      — hours × hourly rate; cost = sell (no markup on top).
   *  - `part`        — units × supplier cost (+ shipping); sell = cost × quote markup.
   *  - `third_party` — sub-contracted work; multi-vendor comparison flow,
   *                    only `vendorQuoteStatus === 'accepted'` rows count
   *                    toward the customer total. Uses snapshotted markup.
   *  - `fixed_price` — flat-rate service line. The customer-facing sell
   *                    price IS the price (typically seeded from the linked
   *                    catalog product's `unitCost`); no buy/shipping/units
   *                    breakdown, no markup. The tech can adjust the sell
   *                    price per-quote without touching the catalog. Use
   *                    this for services that are quoted at a single
   *                    flat number rather than priced from labour + parts.
   */
  kind: 'labour' | 'part' | 'third_party' | 'fixed_price';
  description: string;              // internal note: "1.0h diagnosis + reflow"
  buyPriceExGst: number;            // total cost ex-GST (units × unitPriceExGst
                                    //   [+ shipping for parts]) — the rolled-up
                                    //   value kept on every component for the
                                    //   sake of legacy rows that pre-date the
                                    //   units/unitPrice split. New rows should
                                    //   keep this in sync with the inputs.
  shipping: number;                 // freight for the part (0 for labour)
  sellPrice: number;                // internal sell — sums up to customer price
  productCode?: string | null;      // CatalogProduct.sku. Labour defaults to
                                    //   RCS_REPAIR_LABOUR_SKU; parts default
                                    //   to HW_REPAIR_PART_SKU.
  url?: string | null;              // supplier listing link (parts only —
                                    //   surfaced in the editor for ordering)

  /**
   * Quantity for the component. For `labour` this is hours in 0.25
   * increments (so 1.25 = 1h 15m). For `part` this is a whole-unit count.
   * Optional for backwards compatibility — legacy rows without it are
   * treated as `units = 1`.
   */
  units?: number;

  /**
   * Per-unit ex-GST cost. For `labour` this is the hourly rate (defaults
   * to the `RCS.RepairLabour` catalog `unitCost` on add). For `part` this
   * is the unit-cost ex-GST from the supplier listing. Optional for
   * backwards compatibility — legacy rows without it are treated as
   * `unitPriceExGst = buyPriceExGst` (i.e. the whole component is one
   * unit at the stored cost).
   */
  unitPriceExGst?: number;

  /**
   * Optional component category tag (e.g. "RAM", "Battery", "Screen"). When
   * set, the UI cross-checks against the parent RepairService's
   * componentCategories list and warns on mismatch — never blocks.
   */
  componentType?: string | null;

  /**
   * Fulfillment source for a part component — distinguishes parts the
   * shop already has on hand from parts that need to be ordered from a
   * supplier. Drives:
   *   - the "Send to ordering" flow on the quote detail page, which
   *     only enqueues parts marked `'order'` (or unset, treated as
   *     `'order'` for legacy rows);
   *   - a quote-level summary so the tech/manager can see at a glance
   *     how many parts on this quote will need a PO vs come from stock.
   *
   * Only meaningful for `kind === 'part'`. Defaults to `'order'` when
   * unset.
   */
  fulfillmentSource?: 'stock' | 'order';

  /**
   * GST mode the tech is entering supplier costs in, captured per part
   * line. Independent of the quote's customer-facing `priceDisplayMode`
   * — reflects what the supplier is actually charging us (e.g. a disti
   * invoice is ex-GST, a retail listing is inc-GST), so the same quote
   * can mix parts sourced from both without the math going wrong.
   *
   * Storage convention is unchanged: `unitPriceExGst`, `shipping` and
   * `buyPriceExGst` remain ex-GST internally. This flag only drives the
   * editor's input/display: when `'inc'`, those fields render multiplied
   * by 1.1 and on edit the entered value is divided by 1.1 before being
   * persisted ex-GST.
   *
   * Only meaningful for `kind === 'part'`. Defaults to `'ex'` when unset
   * (legacy rows pre-dating the field).
   */
  supplierGstMode?: 'ex' | 'inc';

  // -------------------------------------------------------------------
  // Third-party kind extras (snapshots from the RepairServiceType at
  // add-time so existing quotes don't re-render if the admin later
  // changes the markup config or swaps the vendor on the type).
  // -------------------------------------------------------------------

  /** Vendor for this component.
   *
   *  - `third_party` kind: the sub-contractor we're getting a quote
   *    from. Initial snapshot comes from the RepairServiceType's
   *    `thirdPartyVendorId`; tech can override per line so multiple
   *    quote-comparison rows pick different vendors.
   *  - `part` kind: the supplier (disti or retailer) we're buying
   *    the part from. Drives the seed for `supplierGstMode` —
   *    retailer ⇒ `'inc'`, otherwise ⇒ `'ex'`. Tech can flip the
   *    GST dropdown afterward.
   *  - `labour` kind: unused.
   */
  vendorId?: string | null;

  /** Display-name snapshot of the vendor, refreshed when the tech
   *  changes the vendor picker. Applies to `third_party` and `part`
   *  components. */
  vendorName?: string | null;

  /**
   * Where this vendor quote sits in the multi-vendor comparison flow.
   * Only `'accepted'` contributes to the customer-facing total — other
   * statuses are kept on the quote for audit / future reference but
   * roll up as $0.
   *
   *  - `'awaiting'`        — reached out, no response yet (default)
   *  - `'received'`        — vendor quoted a price (cost entered)
   *  - `'declined'`        — vendor said no
   *  - `'unable_to_quote'` — couldn't get a quote (timeout, no answer)
   *  - `'accepted'`        — chosen vendor; one per RepairService.
   */
  vendorQuoteStatus?:
    | 'awaiting'
    | 'received'
    | 'declined'
    | 'unable_to_quote'
    | 'accepted';

  /** How sellPrice is derived from cost on a third-party component.
   *   - `'fixed'` — `sellPrice = buyPriceExGst + markupValue`
   *
   * Only `'fixed'` is offered going forward; the union retains `null` for
   * rows that haven't configured a markup yet. */
  markupType?: 'fixed' | null;

  /** Numeric dollar markup paired with `markupType === 'fixed'`. */
  markupValue?: number;

  /**
   * Vendor-quoted lead time for a third-party component, captured as a
   * customer-facing label like `1-2 days`, `2-3 weeks`, `4 weeks`.
   * Accepted format: `^\d+(-\d+)?\s*(days?|weeks?)$` (case-insensitive).
   *
   * Surfaces on the customer-facing quote preview under the accepted
   * vendor's repair service. Part Orders integration (Phase 2 of the
   * Part Orders module) will parse the upper bound to drive an
   * `expected_delivery_date` for tracking.
   *
   * Only meaningful when `kind === 'third_party'`.
   */
  leadTime?: string | null;
}

/**
 * A single customer-facing service line on a repair-shaped quote
 * (`type === 'repair'` or `type === 'network_install'`). On promotion
 * this collapses into exactly one invoice line whose `name` is the
 * `repairServiceCode` (e.g. RCS.LaptopRAMUpgrade, RCS.NetworkInstall) and
 * whose price is the sum of `components[].sellPrice` (multiplied by 1.1
 * for GST). A `network_install` quote carries exactly one such service.
 */
export interface RepairService {
  id: string;                       // uuid
  position: number;                 // display order
  repairServiceCode: string;        // e.g. "RCS.LaptopRAMUpgrade"
  repairServiceName: string;        // snapshot of displayName at pick-time
  description: string | null;      // per-service customer-facing description;
                                    //   falls back to consolidatedDescription
  components: RepairServiceComponent[];

  /**
   * When true, the customer-facing quote preview appends an
   * `Includes: <qty>x <part description>, …` line under this service's
   * description, listing its part components (blank-description parts are
   * skipped). Authored per-service in the editor; defaults to false.
   */
  includePartDetails?: boolean;

  /**
   * When true, the customer-facing quote preview appends a blanket
   * `Includes all parts and labour` line under this service's description.
   * Independent of `includePartDetails`. Authored per-service in the
   * editor's Quote Preview Options group; defaults to false.
   */
  includeAllPartsLabour?: boolean;

  /**
   * Closing bullet appended to this service's itemised `Includes:` block
   * (only rendered when `includePartDetails` is on). Editable per-quote in
   * the editor's Quote Preview Options group.
   *
   *  - `undefined` (legacy / never edited) → the preview uses
   *    `DEFAULT_INCLUDES_CLOSING_LINE`.
   *  - a non-empty string → used verbatim.
   *  - an empty / whitespace-only string → the tech deliberately cleared
   *    it, so the preview omits the closing bullet entirely.
   */
  includesClosingLine?: string;

  /**
   * When true, this entry is NOT a repair service but a standalone product
   * line sold alongside the repair (e.g. a 1TB HDD added to a data-recovery
   * job). It reuses the `RepairService` shape so it lives in the same
   * `repairServices` tree and needs no separate storage, but the editor and
   * customer preview render it as a plain part rather than a repair-service
   * card, and none of the repair-service preview options (`includePartDetails`
   * / `includeAllPartsLabour`) apply.
   *
   * It carries a single `fixed_price` component holding the sell price, so on
   * promotion it becomes its own invoice line (name = `repairServiceCode` =
   * the product SKU, price = that component's sell) exactly like any other
   * service entry — no special promotion handling required. Defaults to
   * false/undefined (a normal repair service).
   */
  isStandalonePart?: boolean;
}

/**
 * Default closing bullet for a repair service's itemised `Includes:` block
 * (see `RepairService.includesClosingLine`). Used by the quote preview when
 * the per-service value is unset, and as the editor field's placeholder.
 */
export const DEFAULT_INCLUDES_CLOSING_LINE =
  'installation and post install testing';

/**
 * Quote-level discount applied when bundling multiple repair services.
 * Rendered as one negative line on the resulting invoice. `amount` is
 * positive (the negation happens server-side on promotion).
 */
export interface BundleDiscount {
  amount: number;
  description: string;
}

/**
 * Diagnostic-fee reimbursement carried over from the customer's
 * original check-in / source-ticket invoice. Applies to any quote
 * whose source job already collected a diagnostic fee, regardless of
 * outcome — a "buy a new computer" quote raised against a repair job
 * gets the same credit as a repair quote would. Renders as one
 * negative `DISC.Diags` line on the promoted invoice with the
 * description `"Reimbursement of diagnostics fee paid on invoice {n}"`.
 *
 * Server-derived on quote read from the linked check-in session's
 * preliminary diagnostic charge net of any waivers (or, as a fallback,
 * from `RCS.Diagnostics*` line items on the source job's paid invoices).
 * Null when the source had no net diagnostic payment (no diag charge,
 * waived, not yet invoiced) or the quote has no source-job linkage.
 *
 * `amount` is the customer-paid (gross/inc-GST) dollar value, mirrored
 * verbatim on the new invoice — no GST math is applied on promotion.
 * This differs from BundleDiscount, which stores ex-GST and is grossed
 * up on promotion.
 */
export interface DiagnosticReimbursement {
  amount: number;
  sourceInvoiceNumber: string;
}

/**
 * Quote list item returned by GET /api/quotes.
 */
export interface QuotesListItem {
  // Identity
  id: string;                       // Dataverse cr001_quotesid
  number: string;                   // e.g. "7260042" — 7 + 2-digit year + per-year 4-digit counter
  name: string;                     // cr001_name (title)

  // State
  status: QuoteStatus;
  type: QuoteType;                  // Workflow category; reads default to 'replacement'
                                    //   for legacy rows that pre-date the type column
  consolidated: boolean;            // When true, the customer-facing quote + the resulting
                                    //   invoice render as one summary line instead of N
                                    //   itemised lines. Reads default by type when null
                                    //   (repair/m365 → true, replacement → false).
  consolidatedDescription: string | null;
                                    // Customer-facing description used by the consolidated
                                    //   render and the resulting invoice line. Auto-seeded
                                    //   on create from the marked options' descriptions
                                    //   (newline-joined); the tech can edit freely.
                                    //   Null falls back to the type-default label
                                    //   (e.g. "Repair Service").
  validUntil: string | null;        // YYYY-MM-DD
  note: string | null;

  // Customer (from expanded cr001_customer_id)
  customerId: string | null;
  customerName: string;
  customerBusinessName: string | null;
  customerFullName: string | null;
  // Contact details, surfaced on the quote detail page so the quoter can
  // reach the customer without leaving the quote. Null when the customer
  // record has no value for the field.
  customerPhone: string | null;
  customerMobile: string | null;
  customerEmail: string | null;

  // Optional ticket linkage
  rsTicketId: string | null;
  rsTicketNumber: string | null;
  sm4rtJobId: string | null;

  /**
   * Links sibling *variations* of one quote. All variations created off a
   * given quote (alternatives for the SAME customer) share this id; the
   * UI groups quotes that share it. Minted when the first variation is
   * created off a standalone quote and back-filled onto the original.
   *
   * Null for standalone quotes that have never been varied (the common
   * case) and for *duplicates* — a duplicate (a copy for a DIFFERENT
   * customer) deliberately starts its own lineage.
   */
  variationGroupId: string | null;

  // Promotion
  invoiceId: string | null;         // DV cr001_invoicesid once promoted
  invoiceNumber: string | null;     // For convenience in list views
  invoicePaid: boolean | null;      // null when no invoice yet; true once the invoice is paid
  promotedAt: string | null;        // ISO timestamp

  /**
   * ISO timestamp set when the quote's line items were **added to a ticket**
   * (generate-ticket with line items included). This — and only this — is what
   * locks a quote on the ticket side: once its lines are on a ticket, editing
   * would desync them, so `ticketGeneratedAt != null` makes the quote
   * terminal/locked, the same way `promotedAt` does for invoices.
   *
   * A quote merely *linked* to a ticket is NOT locked: linking an existing
   * ticket, generating an *empty* ticket, or a quote created *from* a ticket
   * all leave this null and the quote stays editable. Their linkage surfaces
   * via `rsTicketNumber` / `sm4rtJobId` (the "Job #N" chip) instead.
   *
   * When set it also discriminates the locked-state chip: `ticketGeneratedAt`
   * → "Converted to ticket #N"; else `promotedAt` + `invoiceNumber` →
   * "Promoted to invoice N". (A distinct marker is needed because right after
   * invoice promotion `invoiceId` is still null — the orchestrator patches it
   * async — so the invoice link can't tell the two conversions apart.)
   */
  ticketGeneratedAt: string | null;

  /**
   * True when at least one PartOrderLine has been raised against this
   * quote (regardless of current line status — cancelled lines still
   * count). Surfaces in the quotes list so techs know which quotes are
   * already in the procurement pipeline.
   */
  partOrderRaised: boolean;

  // Aggregate sell total (sum of selected options' sellPrice on marked items)
  total: number;

  /**
   * What the quoted service costs the customer — the invoice lines promotion
   * would send, with the EFT buffer baked in and the bundle discount taken
   * off, but NOT the diagnostics credit (a refund of a fee already paid, not
   * part of the service's price). Inc-GST is the sum of the rounded lines;
   * ex-GST is that ÷ 1.1. Null when the response carries no line items — the
   * unscoped list omits them; a job-scoped list (`jobId`) and the detail GET
   * include them. Unlike `total`, this is a number a customer would recognise.
   */
  serviceTotalExGst?: number | null;
  serviceTotalIncGst?: number | null;

  // Pricing
  /**
   * Customer-facing GST display mode. Options' sellPrice is always stored
   * ex-GST; renderers (preview, picker) multiply by 1.1 when this is 'inc'.
   * Defaults on create: 'ex' when the customer has a business name, else 'inc'.
   */
  priceDisplayMode: 'ex' | 'inc';
  /**
   * Quote-level markup applied by the Find Supplier picker to derive each
   * option's sell price from (cost + shipping). Stored as a multiplier-as-
   * fraction (e.g. 0.115 = 11.5%). Default 0.115 on create.
   */
  markup: number;

  // Repair-service fields (populated when type === 'repair', null otherwise)
  /**
   * Hardware form factor for repair quotes. Inherits from the source
   * check-in (`CheckInDraftJob.deviceType`) when present; the tech can
   * also set it explicitly for standalone repair quotes. Null for
   * non-repair quotes and for device-independent services.
   */
  deviceType: RepairDeviceType | null;

  /**
   * Links the repair quote to one specific device (CheckInDraftJob)
   * captured during check-in. When set, QuoteDetailPage live-fetches
   * that device's inspection details from the check-in session so the
   * tech sees who made the device, the serial, brand-new-in-box status
   * and any inspection notes alongside the repair services breakdown.
   *
   * Auto-populated on create from a work item when the work item has
   * exactly one check-in draft job (otherwise left null so the tech
   * picks which device the quote covers).
   *
   * Null for non-repair quotes, standalone repair quotes, and repair
   * quotes whose source work item has no linked check-in.
   */
  sourceCheckInDraftJobId: string | null;
  bundleDiscount: BundleDiscount | null;

  /**
   * Server-derived diagnostic-fee reimbursement carried over from the
   * source ticket/check-in. Populated whenever the quote links to a
   * job whose customer already paid a diagnostic — applies to repair
   * quotes AND new-computer (or other replacement) quotes raised
   * against the same job. Null when the quote has no source-job
   * linkage or the source had no net diagnostic payment. See
   * DiagnosticReimbursement for the promotion-time rendering rules.
   */
  diagnosticReimbursement: DiagnosticReimbursement | null;

  /**
   * EFT-fee buffer flag (effective 2026-10-01). When true, every invoice
   * line is silently uplifted by `eftFeeRate` at promotion-time so the
   * processor fee is absorbed by the price rather than passed through to
   * the customer as a separate line. Defaults to true on new quotes; the
   * tech can opt out per-quote when the customer pays via a method that
   * doesn't incur the fee.
   */
  includeEftFee: boolean;
  /**
   * Effective buffer rate for THIS quote. Persisted as an optional
   * per-quote override in `cr001_eft_fee_rate`; when no override is set
   * the server returns the global default (1.11%). Editable on the quote
   * detail page so techs can match the rate the sourcing vendor charges.
   * Setting it to 0 disables the buffer just like toggling `includeEftFee`
   * off — both paths produce the same net effect.
   */
  eftFeeRate: number;

  /**
   * Slug (`cr001_id_slug`) of the assigned T&C template. Null = render
   * with whichever template currently has `isDefault: true`. Stored as
   * a slug (not the GUID) so quotes survive template rebuilds and a
   * soft-deleted template silently falls back to the default at render
   * time rather than orphaning the row.
   */
  termsTemplateId: string | null;

  // Timestamps
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Friendly sort keys accepted by GET /api/quotes?sort=...
 */
export type QuotesSortField =
  | 'number'
  | 'validUntil'
  | 'total'
  | 'status'
  | 'createdAt';

export interface QuotesListPagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface QuotesListResponse {
  items: QuotesListItem[];
  pagination: QuotesListPagination;
}

/**
 * Full quote returned by GET /api/quotes/{number}.
 *
 * `items` is the legacy item/option tree for new-computer-style quotes.
 * `repairServices` is the nested labour-plus-part shape for repair quotes.
 * Exactly one of the two is populated, discriminated by `type`. Both arrays
 * default to empty so the frontend can pre-init form state safely.
 */
export interface QuoteDetail extends QuotesListItem {
  items: QuoteItem[];
  repairServices: RepairService[];
}

// ---------------------------------------------------------------------------
// DTOs for quote CRUD
// ---------------------------------------------------------------------------

/** Body for POST /api/quotes. */
export interface QuoteCreate {
  name?: string;                    // Defaults server-side to "<customer> — <date>"
  customerId: string;
  sm4rtJobId?: string;              // Optional ticket linkage; RS ticket id + number are
                                    //   read from the linked job on subsequent reads
  type?: QuoteType;                 // Defaults server-side to 'replacement'
  consolidated?: boolean;           // Defaults server-side by type
                                    //   (repair/m365 → true, replacement → false)
  /**
   * Customer-facing description. Three states:
   *   - omit the property entirely → server seeds from the linked job's
   *     latest 'diagnostics' job-log (when type === 'repair' + sm4rtJobId),
   *     else from marked items (existing behaviour).
   *   - pass an explicit string (even "") → use exactly what was passed.
   *   - pass null → store null; render path falls back to the type label.
   */
  consolidatedDescription?: string | null;
  validUntil?: string;
  note?: string;
  items?: QuoteItem[];              // For non-repair quotes
  /**
   * Repair-service entries for `type === 'repair'`. Each entry resolves
   * to one customer-facing invoice line on promotion.
   */
  repairServices?: RepairService[];
  /**
   * Quote-level discount applied when multiple repair services are bundled.
   * Omit / null = no discount.
   */
  bundleDiscount?: BundleDiscount | null;
  /**
   * Hardware form factor for repair quotes. When omitted on creation
   * from a ticket, the server inherits it from the linked check-in
   * (if any). Required for repair quotes that don't have a check-in.
   */
  deviceType?: RepairDeviceType;
  /**
   * Links the new quote to a specific check-in draft job (one device).
   * Set automatically by the work-item "Create Quote" handler when the
   * work item has exactly one check-in device. Optional everywhere
   * else — the tech can pick on the quote page when there are 2+
   * devices to choose from.
   */
  sourceCheckInDraftJobId?: string | null;
  /**
   * Opt out of the EFT-fee buffer for this quote. Omit to accept the
   * server default (currently `true`).
   */
  includeEftFee?: boolean;
  priceDisplayMode?: 'ex' | 'inc';  // Defaults server-side from customer business name
  markup?: number;                  // Defaults server-side to 0.115
  /**
   * Slug of the T&C template to render with this quote. Omit / null =
   * the server resolves to whichever template is currently default.
   */
  termsTemplateId?: string | null;
}

/**
 * Body for POST /api/quotes/quick — walk-in customer + quote in one call.
 *
 * The customer is created server-side from the inline fields below
 * (firstName/lastName/fullName/businessName must include at least one;
 * email/phone/mobile must include at least one) and the new customerId
 * is wired into the quote create automatically — callers do NOT pass
 * `customerId`.
 *
 * Admin-only on the server.
 */
export interface QuickQuoteCreate extends Omit<QuoteCreate, 'customerId'> {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
}

/** Response from POST /api/quotes/quick. */
export interface QuickQuoteResponse {
  quote: QuoteDetail;
  customer: Record<string, unknown> | null;
  customerId: string;
  repairShoprCustomerId?: number | string | null;
}

/** Body for PATCH /api/quotes/{number}. */
export interface QuoteUpdate {
  name?: string;
  status?: QuoteStatus;
  type?: QuoteType;
  consolidated?: boolean;
  consolidatedDescription?: string | null;
  validUntil?: string | null;
  note?: string | null;
  items?: QuoteItem[];              // Full replacement of the items tree
  repairServices?: RepairService[]; // Full replacement of the repair-services tree
  bundleDiscount?: BundleDiscount | null;
  deviceType?: RepairDeviceType;
  /** Persist the picked check-in device for this repair quote. Pass an
   *  explicit `null` to clear the link. */
  sourceCheckInDraftJobId?: string | null;
  includeEftFee?: boolean;
  priceDisplayMode?: 'ex' | 'inc';
  markup?: number;
  /** Slug of the T&C template; null clears the assignment back to default. */
  termsTemplateId?: string | null;
}

/** Body for POST /api/quotes/{number}/promote. */
export interface QuotePromoteRequest {
  /**
   * Override the invoice name on the resulting RS invoice. When
   * omitted, the backend uses the quote's `cr001_name` (which itself
   * defaults to "Quote for <customer> [job <ticket#>]" at create
   * time). Useful when the tech wants the customer-facing invoice
   * title to differ from the internal quote title.
   */
  invoiceName?: string;

  /**
   * Transient per-line description overrides applied to the resulting
   * invoice only — NOT persisted back to the quote (same lifetime as
   * `invoiceName`). Keyed by a stable line key that the frontend and
   * backend derive identically:
   *
   *   - service-shaped quotes (`repair` / `network_install`): the
   *     `RepairService.id` of the service that line collapses from;
   *   - itemised quotes: the `QuoteItem.id` of the marked item;
   *   - consolidated quotes: the sentinel `"consolidated"` (their N
   *     items collapse to a single line).
   *
   * A non-empty string replaces the computed line description; an
   * empty/whitespace value or an unknown key is ignored (the computed
   * description stands). System-generated lines — the bundle-discount
   * and diagnostics-reimbursement credits — have no key and can't be
   * overridden. Lets the tech fill in placeholders like
   * "Laptop Battery Replacement [Manufacturer][Model]" at generation
   * time without editing the quote.
   */
  lineDescriptions?: Record<string, string>;
}

/** Response for POST /api/quotes/{number}/promote. */
export interface QuotePromoteResponse {
  invoiceId: string;
  invoiceNumber: string;
  lineItemCount: number;
}

/**
 * Body for POST /api/quotes/{number}/generate-ticket.
 *
 * Converts the quote into a RepairShopr ticket (a "Job") instead of an
 * invoice. Terminal like promotion: stamps `ticketGeneratedAt` + status
 * `accepted` and locks the quote. The orchestrator back-patches the quote's
 * job lookup once the ticket exists (surfacing via `rsTicketNumber`).
 */
export interface GenerateTicketRequest {
  /**
   * When true, the quote's marked line items are projected onto the new
   * ticket using the SAME logic (and EFT-fee buffer) as invoice promotion.
   * When false, an empty ticket is created.
   */
  includeLineItems: boolean;
  /** Override the ticket subject. Defaults server-side to the quote name. */
  subject?: string;
}

/**
 * Response for POST /api/quotes/{number}/generate-ticket.
 *
 * The ticket is created asynchronously via the orchestrator, so the RS ticket
 * number is NOT known here — it arrives later via a `quotes:changed` /
 * `ticket_generated` SignalR event once the orchestrator has created the
 * ticket and back-patched the quote.
 */
export interface GenerateTicketResponse {
  messageId: string;
  status: string;
  lineItemCount: number;
  quoteNumber: string;
}

/**
 * Body for POST /api/quotes/{number}/link-invoice.
 *
 * Links the quote to an EXISTING invoice (one raised manually, not by SM4RT).
 * No invoice is created and no line items are added — the quote is simply
 * bound to the invoice and marked terminal (reusing the promotion chip/lock).
 */
export interface LinkInvoiceRequest {
  /** RS invoice display number; resolved against the Dataverse invoices mirror. */
  invoiceNumber: string;
}

/** Response for POST /api/quotes/{number}/link-invoice. */
export interface LinkInvoiceResponse {
  invoiceId: string;
  invoiceNumber: string;
  quoteNumber: string;
  /**
   * Non-blocking warning surfaced when the linked invoice's customer differs
   * from the quote's customer (the link still succeeds).
   */
  warning?: string;
}

/**
 * Body for POST /api/quotes/{number}/link-ticket.
 *
 * Links the quote to an EXISTING RepairShopr ticket (a Job raised outside the
 * quote flow). No ticket is created and no line items are added — the quote is
 * bound to the ticket and marked terminal (reusing the ticket chip/lock). A
 * quote may be linked to at most ONE ticket.
 */
export interface LinkTicketRequest {
  /** RS ticket display number; resolved against the Dataverse jobs mirror. */
  ticketNumber: string;
}

/** Response for POST /api/quotes/{number}/link-ticket. */
export interface LinkTicketResponse {
  jobId: string;
  ticketNumber: string;
  quoteNumber: string;
  /**
   * Non-blocking warning surfaced when the linked ticket's customer differs
   * from the quote's customer (the link still succeeds).
   */
  warning?: string;
}

// ---------------------------------------------------------------------------
// Search index — flat option results across all quotes
// ---------------------------------------------------------------------------

/**
 * One row from GET /api/quote-options/search?q=...
 *
 * Flattens an option out of its parent quote so the user can pick a past
 * option and clone its fields into the active quote.
 */
export interface QuoteOptionSearchResult {
  // Source quote
  quoteId: string;
  quoteNumber: string;
  quotedAt: string | null;          // createdAt of source quote (ISO)

  // Item context
  itemName: string;                 // e.g. "Monitor"

  // The option fields (same shape as QuoteOption, minus id)
  description: string;
  buyPriceExGst: number;
  shipping: number;
  url: string;
  sellPrice: number;
  productCode: string;
}

export interface QuoteOptionSearchResponse {
  results: QuoteOptionSearchResult[];
}
